import { Link } from "@tanstack/react-router";
import { ArrowRight, Zap, Check } from "lucide-react";
import { withUTM } from "@/lib/utm";

interface StartupPricingCalloutProps {
  slug?: string;
  className?: string;
}

export function StartupPricingCallout({
  slug = "general",
  className = "",
}: StartupPricingCalloutProps) {
  const contactHref = withUTM("/contact", {
    plan: "essential",
    source: "startup_pricing_callout",
    industry: slug,
  });

  return (
    <section className={`px-6 py-12 ${className}`}>
      <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border-2 border-brand-accent/30 bg-gradient-to-br from-brand-accent/5 via-background to-brand-surface p-8 md:p-12 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wider text-brand-navy">
              <Zap className="size-3.5 text-brand-accent" />
              No Full-Time Bookkeeper Needed
            </div>

            <h3 className="mt-4 font-display text-2xl font-bold text-brand-navy sm:text-3xl">
              Don't need a full-time in-house bookkeeper?
            </h3>

            <p className="mt-3 text-base text-brand-navy/70 leading-relaxed">
              Why pay a $65,000/year salary plus benefits when you only need part-time bookkeeping? Our{" "}
              <strong className="text-brand-navy font-semibold">
                Essential Plan starts from just $199/mo
              </strong>
              . You get monthly bank and credit card reconciliations, transaction categorization in QuickBooks or Xero, monthly P&L and Balance Sheet statements, and CPA tax-ready books with zero setup fees and no long-term contracts.
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-brand-navy/80">
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-brand-accent" /> Up to 75 txns/mo
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-brand-accent" /> Bank & card feed recs
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-brand-accent" /> Monthly P&L & Balance Sheet
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-brand-accent" /> 1099 tracking & CPA ready
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="size-4 text-brand-accent" /> Month-to-month, cancel anytime
              </span>
            </div>
          </div>

          <div className="shrink-0 flex flex-col items-start md:items-end gap-3">
            <div className="text-left md:text-right">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-navy/60">
                Essential Tier
              </span>
              <div className="font-display text-4xl font-extrabold text-brand-navy">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-navy/60 mr-1.5 align-middle">
                  From
                </span>
                $199 <span className="text-lg font-medium text-brand-navy/60">/mo</span>
              </div>
              <span className="text-[11px] font-semibold text-emerald-600 block mt-0.5">
                Save 95%+ vs full-time hire
              </span>
            </div>

            <Link
              to="/contact"
              search={{ plan: "essential", industry: slug }}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-accent px-7 py-3.5 text-sm font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-brand-navy hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
            >
              Get Started from $199 <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
